import {
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    BrandMark
} from "./brand";
import {
    type StepperStepEntry,
    NCoreUIKitTheme,
    InputOTP,
    Stepper,
    Button,
    Avatar,
    Alert,
    Card,
    Text,
    Icon
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    ShieldCheck,
    ArrowLeft,
    Clock,
    Mail
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

const MailIcon = createKitIcon(Mail);

const ClockIcon = createKitIcon(Clock);

const BackIcon = createKitIcon(ArrowLeft);

const RESEND_SECONDS = 42;

const ACTIVE_STEP = 1;

const stylesheet = StyleSheet.create({
    topRow: {
        justifyContent: "flex-end",
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    centerArea: {
        justifyContent: "center",
        alignItems: "center",
        flexGrow: 1
    },
    legalRow: {
        justifyContent: "center",
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    section: {
        flexDirection: "column",
        width: "100%"
    },
    main: {
        flexDirection: "column",
        flex: 1
    },
    backRow: {
        alignSelf: "flex-start"
    },
    heading: {
        flexDirection: "column"
    },
    securityRow: {
        flexDirection: "row",
        alignItems: "center"
    },
    panel: {
        flexShrink: 0
    },
    panelContent: {
        flexGrow: 1
    },
    content: {
        flexGrow: 1
    },
    page: {
        flexGrow: 1
    },
    text: {
        flex: 1
    }
});

const Signup01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        seconds,
        setSeconds
    ] = useState(RESEND_SECONDS);

    const isWide = width >= shapeConfigs.wideBreakpoint;

    const steps: Array<StepperStepEntry> = [
        {
            description: t("blocks.signup01.step1Description"),
            title: t("blocks.signup01.step1")
        },
        {
            description: t("blocks.signup01.step2Description"),
            title: t("blocks.signup01.step2")
        },
        {
            description: t("blocks.signup01.step3Description"),
            title: t("blocks.signup01.step3")
        }
    ];

    useEffect(() => {
        if(seconds <= 0) {
            return undefined;
        }

        const timer = setTimeout(() => setSeconds(previous => previous - 1), 1000);

        return () => clearTimeout(timer);
    }, [
        seconds
    ]);

    const handlePress = () => undefined;

    const handleResendPress = () => setSeconds(RESEND_SECONDS);

    const resendTitle = seconds > 0 ? `${t("blocks.signup01.resend")} (0:${String(seconds).padStart(2, "0")})` : t("blocks.signup01.resend");

    const renderStepper = () => <Stepper
        accessibilityLabel={t("blocks.signup01.stepsLabel")}
        orientation={isWide ? "vertical" : "horizontal"}
        activeIndex={ACTIVE_STEP}
        steps={steps}
    />;

    const renderProgress = () => {
        if(!isWide) {
            return <View
                style={{
                    padding: spaces.spacingMd
                }}
            >
                {renderStepper()}
            </View>;
        }

        return <Card
            style={[
                stylesheet.panel,
                {
                    width: shapeConfigs.cardWidth,
                    margin: spaces.spacingSm
                }
            ]}
            variant="filled"
        >
            <Card.Header>
                <View
                    style={[
                        stylesheet.heading,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <BrandMark/>
                    <View
                        style={[
                            stylesheet.heading,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Card.Title>
                            {t("blocks.signup01.title")}
                        </Card.Title>
                        <Card.Description>
                            {t("blocks.signup01.description")}
                        </Card.Description>
                    </View>
                </View>
            </Card.Header>
            <Card.Content
                style={stylesheet.panelContent}
            >
                {renderStepper()}
            </Card.Content>
            <Card.Footer
                isDivided={false}
            >
                <View
                    style={[
                        stylesheet.securityRow,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Icon
                        icon={ShieldCheck}
                        size="small"
                    />
                    <Text
                        style={stylesheet.text}
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.signup01.security")}
                    </Text>
                </View>
            </Card.Footer>
        </Card>;
    };

    return <ScrollView
        contentContainerStyle={[
            stylesheet.content,
            {
                flexDirection: isWide ? "row" : "column"
            }
        ]}
        style={stylesheet.page}
    >
        {renderProgress()}
        <View
            style={[
                stylesheet.main,
                {
                    padding: isWide ? spaces.spacingLg : spaces.spacingMd,
                    gap: spaces.spacingLg
                }
            ]}
        >
            <View
                style={[
                    stylesheet.topRow,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t("blocks.signup01.hasAccount")}
                </Text>
                <Button
                    title={t("blocks.signup01.signIn")}
                    onPress={handlePress}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </View>
            <View
                style={stylesheet.centerArea}
            >
                <View
                    style={[
                        stylesheet.section,
                        {
                            maxWidth: shapeConfigs.cardWidth + spaces.spacingXxl,
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.heading,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Avatar
                            icon={MailIcon}
                            type="neutral"
                            size="medium"
                        />
                        <Text
                            variant="headlineMediumSize"
                            color="high"
                        >
                            {t("blocks.signup01.verifyTitle")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("blocks.signup01.verifyDescription")}
                        </Text>
                    </View>
                    <InputOTP
                        title={t("blocks.signup01.codeLabel")}
                        initialValue="4829"
                        size="large"
                        length={6}
                    />
                    <View
                        style={[
                            stylesheet.heading,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Button
                            title={t("blocks.signup01.verify")}
                            spreadBehaviour="stretch"
                            onPress={handlePress}
                            variant="filled"
                            type="primary"
                        />
                        <Button
                            onPress={handleResendPress}
                            spreadBehaviour="stretch"
                            isDisabled={seconds > 0}
                            title={resendTitle}
                            icon={ClockIcon}
                            variant="ghost"
                            type="neutral"
                        />
                    </View>
                    <Alert
                        description={t("blocks.signup01.notice")}
                        title={t("blocks.signup01.noticeTitle")}
                        type="info"
                    />
                    <View
                        style={stylesheet.backRow}
                    >
                        <Button
                            title={t("blocks.signup01.changeEmail")}
                            spreadBehaviour="free"
                            onPress={handlePress}
                            icon={BackIcon}
                            variant="link"
                            type="neutral"
                        />
                    </View>
                </View>
            </View>
            <View
                style={[
                    stylesheet.legalRow,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    variant="bodySmallSize"
                    color="mid"
                >
                    {t("blocks.signup01.help")}
                </Text>
                <Button
                    title={t("blocks.signup01.contact")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="link"
                    type="neutral"
                    size="small"
                />
            </View>
        </View>
    </ScrollView>;
};
export default Signup01;
